<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Button, Slider, Text, Toggle } from '@Shelly/shelly-design-system';
	import DoubleTimePicker from 'src/components/TimePicker/DoubleTimePicker.svelte';
	import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { real_id } from 'src/helpers/interface_helpers';
	import type { VentoraFanConfig } from 'src/types/ventora_fan';
	import { VENTORA_NIGHT_SPEED_LIMIT_HINT } from './ventora_fan_field_copy';
	import {
		defaultVentoraFanConfig,
		normalizeVentoraTimeValue,
		patchVentoraFanConfig,
		resolveVentoraFanConfig,
		VENTORA_FAN_COMPONENT_KEY,
		ventoraClamp,
		ventoraHasValidationErrors,
		ventoraNightModeLimits,
		ventoraNumericValidation,
	} from './ventora_fan_settings_helpers';

	export let device;
	export let isOffline: boolean | undefined;
	export let useNewLayout: boolean | undefined;

	const id = real_id(device.id);
	const defaultNight = defaultVentoraFanConfig.night_mode;

	let loading = false;
	let dirty = false;
	let draftEnable = defaultNight.enable;
	let draftLimit = defaultNight.limit;
	let activeBetweenTime: [string, string] = [...defaultNight.active_between];

	$: settingsStore = getDeviceSettings(device.id);
	$: config = resolveVentoraFanConfig(
		$settingsStore?.[VENTORA_FAN_COMPONENT_KEY] as Partial<VentoraFanConfig> | undefined,
	);

	$: if (!dirty) {
		draftEnable = config.night_mode.enable;
		draftLimit = config.night_mode.limit;
		activeBetweenTime = [...config.night_mode.active_between];
	}

	$: timePickerNames = [
		$format('ventora_night_start_time', { default: 'Start time' }),
		$format('ventora_night_end_time', { default: 'End time' }),
	];

	$: limitSliderA11y = {
		sliderValueText: $format('ventora_night_speed_limit_a11y', {
			values: { limit: draftLimit },
			default: 'Night speed limit {limit} percent',
		}),
		ariaLabel: $format('sub_settings_title_ventora_night_limit', { default: 'Night speed limit' }),
		ariaLabelValue: $format('ventora_night_speed_limit_a11y', {
			values: { limit: draftLimit },
			default: 'Night speed limit {limit} percent',
		}),
	};

	$: limitValidation = ventoraNumericValidation(draftLimit, ...ventoraNightModeLimits.limit);
	$: hasValidationErrors = ventoraHasValidationErrors([limitValidation]);

	const commitNightDraft = () => {
		draftLimit = ventoraClamp(draftLimit, ...ventoraNightModeLimits.limit);
		activeBetweenTime = [
			normalizeVentoraTimeValue(activeBetweenTime[0], defaultNight.active_between[0]),
			normalizeVentoraTimeValue(activeBetweenTime[1], defaultNight.active_between[1]),
		];
	};

	const handleTimeChange = () => {
		dirty = true;
	};

	const save = async () => {
		if (!dirty || hasValidationErrors) return;
		commitNightDraft();
		loading = true;
		const ok = await patchVentoraFanConfig(device, {
			night_mode: {
				enable: draftEnable,
				limit: draftLimit,
				active_between: [...activeBetweenTime],
			},
		});
		if (ok) dirty = false;
		loading = false;
	};
</script>

<Text styleType="body-small" class="mb-m text-color-muted">
	{$format('ventora_night_mode_priority_hint', {
		default: 'High priority. Active after Humidity trigger. Overridden by Boost trigger.',
	})}
</Text>

<Toggle
	size="m"
	checked={draftEnable}
	on:click={() => {
		draftEnable = !draftEnable;
		dirty = true;
	}}
	id="toggle-ventora-night-mode"
>
	{$format('label_enable_night_mode', { default: 'Enable night mode' })}
</Toggle>

<Slider
	id="slider-ventora-night-limit"
	class="mt-m mb-m"
	a11yProps={limitSliderA11y}
	min={ventoraNightModeLimits.limit[0]}
	max={ventoraNightModeLimits.limit[1]}
	step={1}
	value={draftLimit}
	type="simple"
	unit="%"
	withOverlay={false}
	stopValueLabel
	title={$format('ventora_night_speed_limit', {
		values: { limit: draftLimit },
		default: 'Speed limit',
	})}
	on:value_change={(e) => {
		draftLimit = e.detail.value;
		dirty = true;
	}}
/>
{#if limitValidation.error}
	<Text styleType="tiny" class="text-danger mb-s">{limitValidation.error}</Text>
{:else if limitValidation.info}
	<Text styleType="tiny" class="text-color-muted mb-s">{limitValidation.info}</Text>
{/if}
<Text styleType="tiny" class="text-color-muted mb-m">
	{$format(VENTORA_NIGHT_SPEED_LIMIT_HINT.key, {
		default: VENTORA_NIGHT_SPEED_LIMIT_HINT.default,
	})}
</Text>

{#key `${config.night_mode.active_between[0]}-${config.night_mode.active_between[1]}`}
	<DoubleTimePicker
		class="mb-m"
		bind:time={activeBetweenTime}
		names={timePickerNames}
		on:change={handleTimeChange}
	/>
{/key}

<Button
	size="s"
	width="full"
	variant="primary"
	disabled={!dirty || hasValidationErrors}
	{loading}
	on:click={save}
	id="button-ventora-night-mode-save"
>
	{$format('button_save', { default: 'Save' })}
</Button>
